import {
  aliasToPublicSignKey,
  setAlias,
  setAvatar,
  setName,
  useIdentityProfile,
} from "@alice-and-bot/core";
import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import {
  Button,
  Collapsible,
  CopyableValue,
  inputClass,
  PageSpinner,
  Spinner,
} from "./components.tsx";
import { useQuery } from "./db.ts";

export const AliceAndBotConfig = ({ id }: { id: string }) => {
  const { data, isLoading, error } = useQuery({
    bots: { $: { where: { id } } },
  });
  const [alias, setLocalAlias] = useState("");
  const [checking, setChecking] = useState(false);
  const [aliasError, setAliasError] = useState("");
  const [profileName, setProfileName] = useState("");
  const [profileAvatar, setProfileAvatar] = useState("");
  if (isLoading) return <PageSpinner />;
  if (error) return error.message;
  const [bot] = data.bots;
  if (!bot) return null;
  const identityProfile = useIdentityProfile(
    bot.aliceAndBotCredentials.publicSignKey,
  );
  return (
    <Collapsible title="Alice & Bot Network">
      <p className="text-sm text-text-secondary-light dark:text-text-secondary mb-3">
        Alice & Bot is a free network where humans and bots can interact
        seamlessly. Your bot has a public identity via its publicSignKey. Set a
        human-friendly alias below.{" "}
        <span className="font-semibold">
          No email or social account required
        </span>{" "}
        - just pick an alias to make it easier for others to find and reference
        you.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Alias</label>
          <input
            type="text"
            value={alias}
            onInput={({ currentTarget: { value } }) => {
              setLocalAlias(value);
              setAliasError("");
            }}
            placeholder="e.g. my-shop-helper"
            className={inputClass}
          />
          {aliasError && (
            <div className="text-xs text-danger mt-1">
              {aliasError}
            </div>
          )}
          {!aliasError && alias && (
            <div className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
              Alias is not yet saved
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => {
              const value = alias.trim().toLowerCase();
              if (!value) return;
              setAliasError("");
              if (!/^[a-z0-9-_]{3,32}$/.test(value)) {
                setAliasError("Use 1-15 chars: a-z0-9_ only");
                return;
              }
              const op = () => {
                setChecking(true);
                return aliasToPublicSignKey(value)
                  .then((res) => {
                    if ("error" in res) return;
                    const existingKey =
                      (res as { publicSignKey?: string }).publicSignKey;
                    if (
                      existingKey &&
                      existingKey !== bot.aliceAndBotCredentials.publicSignKey
                    ) {
                      setAliasError("Alias already taken");
                      return Promise.reject(new Error("Alias already taken"));
                    }
                  })
                  .then(() =>
                    setAlias({
                      credentials: bot.aliceAndBotCredentials,
                      alias: value,
                    })
                  )
                  .then(() => "Alias saved")
                  .finally(() => setChecking(false));
              };
              toast.promise(op(), {
                loading: "Saving alias...",
                success: (msg) => msg,
                error: (e: Error) => e.message || "Failed to save alias",
              });
            }}
            disabled={!alias.trim() || checking}
          >
            {checking ? <Spinner /> : "Save Alias"}
          </Button>
          <CopyableValue value={bot.aliceAndBotCredentials.publicSignKey} />
        </div>
        <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
          Public Sign Key (read-only)
        </div>
        <div className="pt-2 border-t border-border-subtle-light dark:border-border-subtle">
          <div className="text-sm font-medium mb-1">Current Public Profile</div>
          <div className="mb-3 flex items-center gap-3 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-3">
            <div
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-2xl ${
                identityProfile?.avatar
                  ? ""
                  : "bg-gradient-to-br from-accent-light/25 to-accent/40 dark:from-accent/25 dark:to-accent-light/30 ring-1 ring-border-subtle-light dark:ring-border-subtle"
              }`}
            >
              {identityProfile?.avatar
                ? (
                  <img
                    src={identityProfile.avatar}
                    alt="Current Alice & Bot avatar"
                    className="h-full w-full object-cover"
                  />
                )
                : (
                  <div className="flex h-full w-full items-center justify-center text-xl font-semibold text-accent dark:text-accent-light">
                    {(identityProfile?.name ?? bot.name ?? "B").slice(0, 1)
                      .toUpperCase()}
                  </div>
                )}
            </div>
            <div className="min-w-0 text-xs text-text-tertiary-light dark:text-text-tertiary">
              <div className="text-sm font-medium text-text-primary-light dark:text-text-primary">
                Avatar preview
              </div>
              <div className="break-all">
                {identityProfile?.avatar ?? "No avatar set yet."}
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">Name</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={profileName}
                onInput={({ currentTarget: { value } }) =>
                  setProfileName(value)}
                placeholder={identityProfile?.name ?? "e.g. My Bot Name"}
                className={inputClass}
              />
              <Button
                onClick={() => {
                  const trimmed = profileName.trim();
                  if (!trimmed) return;
                  toast.promise(
                    setName({
                      credentials: bot.aliceAndBotCredentials,
                      name: trimmed,
                    }).then((res) => {
                      if (!res.success) {
                        throw new Error(res.error ?? "Failed to set name");
                      }
                      setProfileName("");
                      return "Name saved";
                    }),
                    {
                      loading: "Saving...",
                      success: (msg) => msg,
                      error: (e: Error) => e.message || "Failed to save name",
                    },
                  );
                }}
                disabled={!profileName.trim()}
              >
                Save
              </Button>
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">Avatar URL</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={profileAvatar}
                onInput={({ currentTarget: { value } }) =>
                  setProfileAvatar(value)}
                placeholder={identityProfile?.avatar ??
                  "https://example.com/avatar.png"}
                className={inputClass}
              />
              <Button
                onClick={() => {
                  const trimmed = profileAvatar.trim();
                  toast.promise(
                    setAvatar({
                      credentials: bot.aliceAndBotCredentials,
                      avatar: trimmed || undefined,
                    }).then((res) => {
                      if (!res.success) {
                        throw new Error(res.error ?? "Failed to set avatar");
                      }
                      setProfileAvatar("");
                      return trimmed ? "Avatar saved" : "Avatar removed";
                    }),
                    {
                      loading: "Saving...",
                      success: (msg) => msg,
                      error: (e: Error) => e.message || "Failed to save avatar",
                    },
                  );
                }}
              >
                Save
              </Button>
            </div>
            <div className="mt-1 text-xs text-text-tertiary-light dark:text-text-tertiary">
              Paste a public image URL. Leave empty and save to remove the
              avatar.
            </div>
          </div>
          {identityProfile
            ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {Object.entries(identityProfile)
                  .filter(([_, v]) => v != null && v !== "")
                  .map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                      <span className="uppercase tracking-wide text-text-tertiary-light dark:text-text-tertiary font-semibold">
                        {k}
                      </span>
                      <span className="text-text-primary-light dark:text-text-primary break-all">
                        {String(v)}
                      </span>
                    </div>
                  ))}
                {Object.entries(identityProfile).filter(([_, v]) =>
                      v != null && v !== ""
                    ).length === 0 && (
                  <div className="text-text-tertiary-light dark:text-text-tertiary col-span-full">
                    No profile fields set yet.
                  </div>
                )}
              </div>
            )
            : (
              <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                Profile unavailable.
              </div>
            )}
        </div>
      </div>
    </Collapsible>
  );
};
